// Auth0 Configuration & Integration
// Composable for Auth0 authentication flow

import { useAuthStore } from "~/stores/auth";

// PSD-135069: UTM parameters used in cross-domain login_hint payload.
// Per-key values replace defaults; the full object form carries the analytics UTMs.
// partner is onepassmobile across all flows for now (onepass migration deferred).
//
// IGNITE-71 / PSD-139737: signup_experience=onepassmobile is sent alongside it.
// OnePass renders the OnePass Mobile signup journey when either partner=onepassmobile
// or signup_experience=onepassmobile is present, so sending both is backwards
// compatible and is what makes the later partner=onepass switch safe. Whoever does
// that switch must not remove signup_experience, or the OPM journey breaks.
type UtmOverride = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  partner?: string;
  signup_experience?: string;
};
// Backward-compatible: callers can pass a plain string which is treated as utm_content
type UtmInput = string | UtmOverride;

/**
 * Helper: Check if user is OnePass by reading auth store or sessionStorage fallback.
 * Works on iOS WebView because auth_session_data is refreshed from server on every session check.
 */
function checkIsOnePassFromSession(): boolean {
  if (typeof window === "undefined") return false;

  // Primary: check localStorage flag (works on web)
  if (localStorage.getItem("auth_method") === "onepass") return true;

  // Fallback: derive from server session data (works on iOS WebView)
  try {
    const sessionData = sessionStorage.getItem("auth_session_data");
    if (sessionData) {
      const parsed = JSON.parse(sessionData);
      return parsed.provider === "auth0";
    }
  } catch { /* ignore */ }

  return false;
}

/**
 * Helper: Get refresh token from auth store or sessionStorage fallback.
 */
function getRefreshTokenFromSession(): string | null {
  if (typeof window === "undefined") return null;

  // Primary: localStorage (works on web)
  const localToken = localStorage.getItem("auth0_refresh_token");
  if (localToken) return localToken;

  // Fallback: derive from server session data (works on iOS WebView)
  try {
    const sessionData = sessionStorage.getItem("auth_session_data");
    if (sessionData) {
      const parsed = JSON.parse(sessionData);
      return parsed.onepassInfo?.refreshToken || null;
    }
  } catch { /* ignore */ }

  return null;
}

export const useAuth0 = () => {
  const config = useRuntimeConfig();

  // Auth0 Configuration from runtime config (environment-aware)
  const auth0Config = {
    domain: config.public.ONEPASS_AUTH0_DOMAIN,
    clientId: config.public.ONEPASS_CLIENT_ID,
    redirectUri: config.public.ONEPASS_REDIRECT_URI,
    scope: config.public.ONEPASS_SCOPE || "openid profile email",
  };

  // Validate configuration
  if (
    !auth0Config.domain ||
    !auth0Config.clientId ||
    !auth0Config.redirectUri
  ) {
    console.error(
      "[Auth0] Missing configuration. Check ONEPASS_ENVIRONMENT in .env.server:",
      {
        environment: config.public.ONEPASS_ENVIRONMENT,
        domain: auth0Config.domain,
        clientId: auth0Config.clientId ? "✓" : "✗",
        redirectUri: auth0Config.redirectUri,
      }
    );
    throw new Error("Auth0 configuration incomplete");
  }

  // PKCE Helper Functions (from original component)
  function base64URLEncode(buffer: Uint8Array): string {
    return btoa(String.fromCharCode(...buffer))
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=/g, "");
  }

  async function generateCodeVerifier(): Promise<string> {
    const randomBytes = new Uint8Array(32);
    crypto.getRandomValues(randomBytes);
    return base64URLEncode(randomBytes);
  }

  async function generateCodeChallenge(verifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const hash = await crypto.subtle.digest("SHA-256", data);
    return base64URLEncode(new Uint8Array(hash));
  }

  /**
   * Build login_hint for OnePass SSO
   * @param operation - "login" or "signup"
   * @param userInfo - Optional user info for pre-filling signup form
   * @param utmOverride - Optional UTM params (PSD-135069 Optus spec). Per-key values
   * replace the defaults in the url_search_params payload.
   */
  const buildLoginHint = (
    operation: "login" | "signup" = "login",
    userInfo?: { firstName?: string; lastName?: string; email?: string },
    utmInput?: UtmInput
  ): string => {
    // Build url_search_params - SIT requires mobile_verification_variant=b
    const environment = config.public.ONEPASS_ENVIRONMENT || "SIT";
    const utmOverride: UtmOverride =
      typeof utmInput === "string" ? { utm_content: utmInput } : (utmInput ?? {});

    const params = new URLSearchParams();
    params.set("partner", utmOverride.partner ?? "onepassmobile");
    // IGNITE-71: identifies the OPM signup journey independently of partner
    params.set(
      "signup_experience",
      utmOverride.signup_experience ?? "onepassmobile"
    );
    if (environment !== "PROD") {
      params.set("mobile_verification_variant", "b");
    }
    params.set("utm_source", utmOverride.utm_source ?? "onepass_mobile");
    params.set("utm_medium", utmOverride.utm_medium ?? "referral-web");
    params.set("utm_campaign", utmOverride.utm_campaign ?? "always_on");
    if (utmOverride.utm_content !== undefined) {
      params.set("utm_content", utmOverride.utm_content);
    }
    if (utmOverride.utm_term !== undefined) {
      params.set("utm_term", utmOverride.utm_term);
    }
    const urlSearchParams = params.toString();

    const loginHint = {
      url_search_params: btoa(urlSearchParams),
      auth: {
        operation: operation,
        linking: false,
        subscription_redirect: false,
      },
      user: {
        first_name: userInfo?.firstName || "",
        last_name: userInfo?.lastName || "",
        email: userInfo?.email || "",
      },
    };

    return JSON.stringify(loginHint);
  };

  /**
   * Auth0 Login/Signup Function
   * @param returnUrl - URL to redirect after authentication
   * @param operation - "login" or "signup" (default: "login")
   * @param userInfo - Optional user info for pre-filling signup form
   * @param utmOverride - Optional UTM params per PSD-135069 Optus spec
   */
  const loginWithAuth0 = async (
    returnUrl?: string,
    operation: "login" | "signup" = "login",
    userInfo?: { firstName?: string; lastName?: string; email?: string },
    utmInput?: UtmInput
  ) => {
    try {
      // Generate PKCE parameters
      const codeVerifier = await generateCodeVerifier();
      const codeChallenge = await generateCodeChallenge(codeVerifier);

      // Store verifier and challenge in session storage
      sessionStorage.setItem("auth0_code_verifier", codeVerifier);
      sessionStorage.setItem("auth0_code_challenge", codeChallenge);
      
      // DEBUG: Log the generated code_verifier for mobile debugging
      console.log("[Auth0] 🔐 Generated code_verifier:", codeVerifier);
      console.log("[Auth0] 🔐 Generated code_challenge:", codeChallenge);

      // Store operation type (login vs signup) for callback handling
      // This is used to determine behavior when user is not linked
      sessionStorage.setItem("auth0_operation", operation);

      // Store return URL if provided (for post-login redirect)
      if (returnUrl) {
        sessionStorage.setItem("auth0_return_url", returnUrl);
      }

      // Build Auth0 authorization URL
      const authUrl = new URL(`https://${auth0Config.domain}/authorize`);
      authUrl.searchParams.set("response_type", "code");
      authUrl.searchParams.set("client_id", auth0Config.clientId);
      authUrl.searchParams.set("redirect_uri", auth0Config.redirectUri);
      authUrl.searchParams.set("scope", auth0Config.scope);
      authUrl.searchParams.set("code_challenge", codeChallenge);
      authUrl.searchParams.set("code_challenge_method", "S256");

      // Add login_hint for OnePass SSO (differentiates login vs signup)
      const loginHint = buildLoginHint(operation, userInfo, utmInput);
      authUrl.searchParams.set("login_hint", loginHint);

      // Add screen_hint=signup to force signup screen for signup operation
      if (operation === "signup") {
        authUrl.searchParams.set("screen_hint", "signup");
      }

      // Add user info as separate URL parameters for prefilling Auth0 signup form
      // These are passed as top-level query parameters per Tarak's recommendation
      if (userInfo?.email) {
        // Note: email is already in JSON login_hint, but some flows may need it separately
        console.log(`[Auth0] Including email prefill: ${userInfo.email}`);
      }
      if (userInfo?.firstName) {
        authUrl.searchParams.set("first_name", userInfo.firstName);
      }
      if (userInfo?.lastName) {
        authUrl.searchParams.set("last_name", userInfo.lastName);
      }

      console.log(`[Auth0] Initiating ${operation} flow with login_hint`, { 
        hasEmail: !!userInfo?.email,
        hasFirstName: !!userInfo?.firstName,
        hasLastName: !!userInfo?.lastName,
        screenHint: operation === "signup" ? "signup" : "none"
      });

      // Redirect to Auth0
      window.location.href = authUrl.toString();
    } catch (error) {
      console.error("Auth0 login error:", error);
      throw error;
    }
  };

  /**
   * Build Auth0 SSO params for Flutter WebView without triggering a redirect.
   * Returns the auth URL + codeVerifier so Flutter can open the URL in a modal
   * and inject the verifier into that webview's sessionStorage.
   */
  const buildFlutterSsoParams = async (
    returnUrl: string,
    operation: "login" | "signup" = "login",
    userInfo?: { firstName?: string; lastName?: string; email?: string },
    utmInput?: UtmInput,
    state?: string
  ): Promise<{ authUrl: string; codeVerifier: string }> => {
    const codeVerifier = await generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);

    sessionStorage.setItem("auth0_code_verifier", codeVerifier);
    sessionStorage.setItem("auth0_code_challenge", codeChallenge);
    sessionStorage.setItem("auth0_operation", operation);
    if (returnUrl) sessionStorage.setItem("auth0_return_url", returnUrl);

    const authUrl = new URL(`https://${auth0Config.domain}/authorize`);
    authUrl.searchParams.set("response_type", "code");
    authUrl.searchParams.set("client_id", auth0Config.clientId);
    authUrl.searchParams.set("redirect_uri", auth0Config.redirectUri);
    authUrl.searchParams.set("scope", auth0Config.scope);
    authUrl.searchParams.set("code_challenge", codeChallenge);
    authUrl.searchParams.set("code_challenge_method", "S256");

    if (state) {
      authUrl.searchParams.set("state", state);
    }

    const loginHint = buildLoginHint(operation, userInfo, utmInput);
    authUrl.searchParams.set("login_hint", loginHint);

    if (operation === "signup") {
      authUrl.searchParams.set("screen_hint", "signup");
    }
    if (userInfo?.firstName) authUrl.searchParams.set("first_name", userInfo.firstName);
    if (userInfo?.lastName) authUrl.searchParams.set("last_name", userInfo.lastName);

    return { authUrl: authUrl.toString(), codeVerifier };
  };

  /**
   * Auth0 Signup Function - Convenience wrapper for signup operation
   * @param returnUrl - URL to redirect after signup
   * @param userInfo - Optional user info for pre-filling signup form
   * @param utmOverride - Optional UTM params per PSD-135069 Optus spec
   */
  const signupWithAuth0 = async (
    returnUrl?: string,
    userInfo?: { firstName?: string; lastName?: string; email?: string },
    utmInput?: UtmInput
  ) => {
    return loginWithAuth0(returnUrl, "signup", userInfo, utmInput);
  };

  // Handle Auth0 Callback
  const handleCallback = async (code: string) => {
    try {
      const codeVerifier = sessionStorage.getItem("auth0_code_verifier");
      const codeChallenge = sessionStorage.getItem("auth0_code_challenge");
      
      // DEBUG: Log the retrieved code_verifier for mobile debugging
      console.log("[Auth0] 🔐 Retrieved code_verifier from sessionStorage:", codeVerifier);
      console.log("[Auth0] 🔐 Retrieved code_challenge from sessionStorage:", codeChallenge);
      console.log("[Auth0] 🔐 Authorization code (first 20 chars):", code?.substring(0, 20));
      
      if (!codeVerifier) {
        throw new Error("Code verifier not found");
      }

      // Exchange code for tokens via Strapi
      // Include code_challenge for debugging WebView PKCE issues
      const response = await $fetch("/api/connect/auth0/callback", {
        method: "POST",
        body: {
          code,
          code_verifier: codeVerifier,
          code_challenge: codeChallenge, // For debugging only
        },
      });

      // Clear verifier and challenge
      sessionStorage.removeItem("auth0_code_verifier");
      sessionStorage.removeItem("auth0_code_challenge");

      return response;
    } catch (error) {
      console.error("Auth0 callback error:", error);
      throw error;
    }
  };

  /**
   * Auth0 RP-Initiated Logout (OpenID Connect compliant)
   *
   * Security: Terminates the Auth0/OnePass session to prevent:
   * - Session persistence after user logout
   * - Automatic re-authentication on next OnePass login attempt
   * - Session fixation attacks
   *
   * @param returnTo - URL to redirect after logout (defaults to origin)
   * @param federated - If true, also logs out from identity provider (OnePass)
   */
  const logoutFromAuth0 = (returnTo?: string, federated: boolean = true) => {
    try {
      // Build Auth0 logout URL per RFC 6749 § 10.16
      const logoutUrl = new URL(`https://${auth0Config.domain}/v2/logout`);
      logoutUrl.searchParams.set("client_id", auth0Config.clientId);

      // Return URL after logout - must be in Auth0 Allowed Logout URLs
      const returnUrl = returnTo || window.location.origin;
      logoutUrl.searchParams.set("returnTo", returnUrl);

      // Federated logout - also terminates upstream IdP session (OnePass)
      // This ensures complete logout from the entire SSO chain
      if (federated) {
        logoutUrl.searchParams.set("federated", "");
      }

      console.log(
        "[Auth0] Initiating RP-Initiated Logout:",
        logoutUrl.toString()
      );

      // Clear any stored PKCE parameters and tokens before redirect
      sessionStorage.removeItem("auth0_code_verifier");
      sessionStorage.removeItem("auth0_state");
      localStorage.removeItem("auth0_refresh_token");

      // Redirect to Auth0 logout
      window.location.href = logoutUrl.toString();
    } catch (error) {
      console.error("[Auth0] Logout error:", error);
      // Don't throw - allow fallback to local logout
    }
  };

  /**
   * Proactively refresh Auth0 tokens using stored refresh_token.
   * Returns fresh decoded id_token claims (including updated mobileVerified).
   * If refresh_token rotation is enabled in Auth0, the new refresh_token is saved.
   */
  const refreshAuth0Tokens = async (): Promise<{
    claims: any;
    onepassClaims: any;
  } | null> => {
    try {
      const refreshToken = getRefreshTokenFromSession();
      if (!refreshToken) {
        console.log("[Auth0] No refresh_token found (localStorage or session), cannot refresh");
        return null;
      }

      console.log("[Auth0] Refreshing tokens via refresh_token grant...");

      const response = await fetch(
        `https://${auth0Config.domain}/oauth/token`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            grant_type: "refresh_token",
            client_id: auth0Config.clientId,
            refresh_token: refreshToken,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        console.error("[Auth0] Token refresh failed:", errorData);
        // If refresh token is invalid/expired, clear it
        if (response.status === 403 || response.status === 401) {
          localStorage.removeItem("auth0_refresh_token");
        }
        return null;
      }

      const tokenData = await response.json();
      console.log("[Auth0] Token refresh successful");

      // Save new refresh_token if rotation is enabled (Auth0 returns a new one)
      if (tokenData.refresh_token) {
        localStorage.setItem("auth0_refresh_token", tokenData.refresh_token);
        console.log("[Auth0] Updated refresh_token (rotation)");
      }

      // Decode id_token to get fresh claims
      if (!tokenData.id_token) {
        console.log("[Auth0] No id_token in refresh response");
        return null;
      }

      // Decode JWT payload (base64url)
      const parts = tokenData.id_token.split(".");
      if (parts.length !== 3) return null;

      const payload = JSON.parse(
        atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"))
      );

      const onepassClaims = payload["https://onepass.com.au"] || null;

      console.log("[Auth0] Fresh claims:", {
        email: payload.email,
        mobileVerified: onepassClaims?.mobile_verified,
        emailVerified: onepassClaims?.email_verified,
      });

      return { claims: payload, onepassClaims };
    } catch (error) {
      console.error("[Auth0] Token refresh error:", error);
      return null;
    }
  };

  /**
   * Check if user was authenticated via OnePass SSO
   * Uses auth store, localStorage flag, or sessionStorage auth_session_data as fallback (iOS WebView)
   */
  const isOnePassUser = (): boolean => {
    if (typeof window === 'undefined') return false;

    // Check auth store first (most reliable, populated from /me endpoint)
    const authStore = useAuthStore();
    if (authStore.user?.provider === "auth0") {
      console.log('[Auth0] isOnePassUser: true (from auth store provider)');
      return true;
    }

    // Fallback: localStorage flag or sessionStorage session data
    const isOnePass = checkIsOnePassFromSession();
    console.log('[Auth0] isOnePassUser check:', isOnePass);
    return isOnePass;
  };

  /**
   * Mark current session as OnePass authenticated
   * Called after successful OnePass login
   */
  const setOnePassAuthenticated = () => {
    if (typeof window !== 'undefined') {
      localStorage.setItem("auth_method", "onepass");
      console.log('[Auth0] Marked session as OnePass authenticated');
    }
  };

  /**
   * Clear OnePass authentication flag
   * Called during logout
   */
  const clearOnePassAuthenticated = () => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem("auth_method");
      console.log('[Auth0] Cleared OnePass authentication flag');
    }
  };

  return {
    auth0Config,
    loginWithAuth0,
    signupWithAuth0,
    buildFlutterSsoParams,
    handleCallback,
    logoutFromAuth0,
    isOnePassUser,
    setOnePassAuthenticated,
    clearOnePassAuthenticated,
    refreshAuth0Tokens,
  };
};
